# GraphQL

The SEEK API uses [GraphQL] to expose a graph of [positions], [candidates] and other business objects.

GraphQL allows you to query and join SEEK’s objects together in whatever way best suits your software.
It also gives SEEK insight into how its API is being used and which fields our partners are interested in.

For more background on GraphQL we recommend the GraphQL Foundation’s [Introduction to GraphQL].

## Requests and responses

You can send any GraphQL [operation] that you see on this site or later create in the [GraphQL Explorer] as an HTTP POST request.
GraphQL is not tied to a certain programming language or client library;
this works with any HTTP client that you already use and are familiar with.

[GraphQL Explorer]: /graphql/playground#browser-usage
[operation]: /graphql/in-practice#operations

A simple query can be bundled into a JSON request body like so:

```http
POST https://graphql.seek.com/graphql HTTP/2
Accept-Language: en-AU
Authorization: Bearer PARTNER_TOKEN_HERE
Content-Type: application/json
User-Agent: example-application/1.2.3
X-Request-Id: a9e3d78d-576d-470b-b1d2-52f4921be25c
X-Session-Id: b5a8774c-c450-4906-a25c-861bce129106

{
  "query": "{ version }"
}
```

```http
HTTP/2 200 OK
Content-Language: en-AU
Content-Type: application/json; charset=utf-8

{
  "data": {
    "version": "abcdef0.1234"
  }
}
```

Here are some basic code samples that operate at the HTTP layer.
In practice,
we recommend using a [GraphQL client library] to streamline development.

[GraphQL client library]: /graphql/in-practice#client-libraries-and-tools

```bash
#!/bin/sh

# In practice, propagate language preferences from the end-user client.
# https://developer.seek.com/graphql/in-practice#content-localisation
acceptLanguage='en-AU'

# In practice, retrieve and cache an access token dynamically at runtime.
# https://developer.seek.com/auth
accessToken=''

# In practice, use your application name and version from configuration.
# https://developer.seek.com/graphql/in-practice#tracing-requests
userAgent='example-application/1.2.3'

# In practice, share the session ID among requests in a given interaction.
# https://developer.seek.com/graphql/in-practice#tracing-requests
sessionId="$(uuidgen)"

# In practice, be wary of code injection.
# https://developer.seek.com/graphql/in-practice#variables
data='{"query": "{ version }"}'

# In practice, use a GraphQL client and something other than a Bash script.
# https://developer.seek.com/graphql/in-practice#client-libraries-and-tools
# https://graphql.org/community/tools-and-libraries/?tags=client
response="$(
  curl \
  --data "${data}" \
  --header "Accept-Language: ${acceptLanguage}" \
  --header "Authorization: Bearer ${accessToken}" \
  --header 'Content-Type: application/json' \
  --header "User-Agent: ${userAgent}" \
  --header "X-Request-Id: $(uuidgen)" \
  --header "X-Session-Id: ${sessionId}" \
  --request POST \
  --silent \
  --write-out '\n%{http_code}' \
  'https://graphql.seek.com/graphql'
)"

echo "${response}" | tail -n 1
# 200

echo "${response}" | head -n 1
# {"data":{"version":"abcdefa.12345"},"extensions":{"requestLatency":1}}
```

```csharp
using System.Net.Http.Headers;
using System.Text;

class Program
{
  static async Task Main()
  {
    // In practice, propagate language preferences from the end-user client.
    // https://developer.seek.com/graphql/in-practice#content-localisation
    var acceptLanguage = "en-AU";

    // In practice, retrieve and cache an access token dynamically at runtime.
    // https://developer.seek.com/auth
    var accessToken = "";

    // In practice, use your application name and version from configuration.
    // https://developer.seek.com/graphql/in-practice#tracing-requests
    var userAgent = "example-application/1.2.3";

    // In practice, share the session ID among requests in a given interaction.
    // https://developer.seek.com/graphql/in-practice#tracing-requests
    var sessionId = Guid.NewGuid().ToString(); // V4 UUID

    // In practice, use a GraphQL client.
    // https://developer.seek.com/graphql/in-practice#client-libraries-and-tools
    // https://graphql.org/community/tools-and-libraries/?tags=client_c-net
    using (var httpClient = new HttpClient())
    {
      var request = new HttpRequestMessage(HttpMethod.Post, "https://graphql.seek.com/graphql");
      request.Headers.AcceptLanguage.Add(new StringWithQualityHeaderValue(acceptLanguage));
      request.Headers.Authorization = new AuthenticationHeaderValue("Bearer", accessToken);
      request.Headers.UserAgent.ParseAdd(userAgent);
      request.Headers.Add("X-Request-Id", Guid.NewGuid().ToString());
      request.Headers.Add("X-Session-Id", sessionId);
      // In practice, be wary of code injection.
      // https://developer.seek.com/graphql/in-practice#variables
      request.Content = new StringContent("{\"query\": \"{ version }\"}", Encoding.UTF8, "application/json");

      var response = await httpClient.SendAsync(request);
      var responseBody = await response.Content.ReadAsStringAsync();

      Console.WriteLine((int)response.StatusCode);
      // 200

      Console.WriteLine(responseBody);
      // {"data":{"version":"abcdefa.12345"},"extensions":{"requestLatency":1}}
    }
  }
}
```

```java
import java.io.IOException;
import java.lang.InterruptedException;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.net.URI;
import java.net.URISyntaxException;
import java.util.UUID;

class Main {
  public static void main(String[] args) throws URISyntaxException, IOException, InterruptedException {
    // In practice, propagate language preferences from the end-user client.
    // https://developer.seek.com/graphql/in-practice#content-localisation
    var acceptLanguage = "en-AU";

    // In practice, retrieve and cache an access token dynamically at runtime.
    // https://developer.seek.com/auth
    var accessToken = "";

    // In practice, use your application name and version from configuration.
    // https://developer.seek.com/graphql/in-practice#tracing-requests
    var userAgent = "example-application/1.2.3";

    // In practice, share the session ID among requests in a given interaction.
    // https://developer.seek.com/graphql/in-practice#tracing-requests
    var sessionId = UUID.randomUUID();

    // In practice, use a GraphQL client.
    // https://developer.seek.com/graphql/in-practice#client-libraries-and-tools
    // https://graphql.org/community/tools-and-libraries/?tags=client_java-kotlin-android
    var client = HttpClient.newHttpClient();

    var request = HttpRequest.newBuilder()
      .uri(new URI("https://graphql.seek.com/graphql"))
      .header("Accept-Language", acceptLanguage)
      .header("Authorization", "Bearer " + accessToken)
      .header("Content-Type", "application/json")
      .header("User-Agent", userAgent)
      .header("X-Request-Id", UUID.randomUUID().toString())
      .header("X-Session-Id", sessionId.toString())
      // In practice, be wary of code injection.
      // https://developer.seek.com/graphql/in-practice#variables
      .POST(HttpRequest.BodyPublishers.ofString("{\"query\": \"{ version }\"}"))
      .build();

    // In practice, use `sendAsync()`.
    var response = client
      .send(request, HttpResponse.BodyHandlers.ofString());

    System.out.println(response.statusCode());
    // 200

    System.out.println(response.body());
    // {"data":{"version":"abcdefa.12345"},"extensions":{"requestLatency":1}}
  }
}
```

```javascript label="JavaScript (Node.js)"
import { randomUUID } from 'node:crypto';

// In practice, propagate language preferences from the end-user client.
// https://developer.seek.com/graphql/in-practice#content-localisation
const acceptLanguage = 'en-AU';

// In practice, retrieve and cache an access token dynamically at runtime.
// https://developer.seek.com/auth
const accessToken = '';

// In practice, use your application name and version from configuration.
// https://developer.seek.com/graphql/in-practice#tracing-requests
const userAgent = 'example-application/1.2.3';

// In practice, share the session ID among requests in a given interaction.
// https://developer.seek.com/graphql/in-practice#tracing-requests
const sessionId = randomUUID();

// In practice, use a GraphQL client.
// https://developer.seek.com/graphql/in-practice#client-libraries-and-tools
// https://graphql.org/community/tools-and-libraries/?tags=client_javascript
const response = await fetch('https://graphql.seek.com/graphql', {
  // In practice, be wary of code injection.
  // https://developer.seek.com/graphql/in-practice#variables
  body: JSON.stringify({ query: '{ version }' }),
  headers: {
    'Accept-Language': acceptLanguage,
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
    'User-Agent': userAgent,
    'X-Request-Id': randomUUID(),
    'X-Session-Id': sessionId
  },
  method: 'POST'
});

// In practice, use `json()`.
const responseBody = await response.text();

console.log(response.status);
// 200

console.log(responseBody);
// {"data":{"version":"abcdefa.12345"},"extensions":{"requestLatency":1}}
```

```kotlin
import java.net.HttpURLConnection
import java.net.URL
import java.util.UUID

fun main() {
  // In practice, propagate language preferences from the end-user client.
  // https://developer.seek.com/graphql/in-practice#content-localisation
  val acceptLanguage = "en-AU"

  // In practice, retrieve and cache an access token dynamically at runtime.
  // https://developer.seek.com/auth
  val accessToken = ""

  // In practice, use your application name and version from configuration.
  // https://developer.seek.com/graphql/in-practice#tracing-requests
  val userAgent = "example-application/1.2.3"

  // In practice, share the session ID among requests in a given interaction.
  // https://developer.seek.com/graphql/in-practice#tracing-requests
  val sessionId = UUID.randomUUID().toString()

  // In practice, use a GraphQL client.
  // https://developer.seek.com/graphql/in-practice#client-libraries-and-tools
  // https://graphql.org/community/tools-and-libraries/?tags=client_java-kotlin-android
  val url = URL("https://graphql.seek.com/graphql")
  val connection = url.openConnection() as HttpURLConnection
  connection.setRequestMethod("POST")
  connection.setRequestProperty("Accept-Language", acceptLanguage)
  connection.setRequestProperty("Authorization", "Bearer $accessToken")
  connection.setRequestProperty("Content-Type", "application/json")
  connection.setRequestProperty("User-Agent", userAgent)
  connection.setRequestProperty("X-Request-Id", UUID.randomUUID().toString())
  connection.setRequestProperty("X-Session-Id", sessionId)
  connection.setDoOutput(true)
  // In practice, be wary of code injection.
  // https://developer.seek.com/graphql/in-practice#variables
  connection.outputStream.use { it.write("{\"query\":\"{ version }\"}".toByteArray())}

  val responseBody = connection.inputStream.bufferedReader().use { it.readText() }

  println(connection.responseCode)
  // 200

  println(responseBody)
  // {"data":{"version":"abcdefa.12345"},"extensions":{"requestLatency":1}}
}
```

```php
<?php

// In practice, propagate language preferences from the end-user client.
// https://developer.seek.com/graphql/in-practice#content-localisation
$acceptLanguage = 'en-AU';

// In practice, retrieve and cache an access token dynamically at runtime.
// https://developer.seek.com/auth
$accessToken = '';

// In practice, use your application name and version from configuration.
// https://developer.seek.com/graphql/in-practice#tracing-requests
$userAgent = 'example-application/1.2.3';

// In practice, share the session ID among requests in a given interaction.
// In practice, use proper V4 UUIDs.
// https://developer.seek.com/graphql/in-practice#tracing-requests
$sessionId = bin2hex(random_bytes(16));

// In practice, use a GraphQL client.
// https://developer.seek.com/graphql/in-practice#client-libraries-and-tools
// https://www.google.com/search?q=PHP+GraphQL+client
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, 'https://graphql.seek.com/graphql');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Accept-Language: $acceptLanguage",
  "Authorization: Bearer $accessToken",
  "Content-Type: application/json",
  "User-Agent: $userAgent",
  // In practice, use proper V4 UUIDs.
  // https://developer.seek.com/graphql/in-practice#tracing-requests
  "X-Request-Id: " . bin2hex(random_bytes(16)),
  "X-Session-Id: $sessionId"
]);
curl_setopt($ch, CURLOPT_POST, true);
// In practice, be wary of code injection.
// https://developer.seek.com/graphql/in-practice#variables
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(['query' => '{ version }']));

$responseBody = curl_exec($ch);
$responseStatusCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);

curl_close($ch);

echo $responseStatusCode . "\n";
// 200

echo $responseBody . "\n";
// {"data":{"version":"abcdefa.12345"},"extensions":{"requestLatency":1}}

?>
```

```python label="Python 3"
import json
import urllib.request
import urllib.parse
import uuid

# In practice, propagate language preferences from the end-user client.
# https://developer.seek.com/graphql/in-practice#content-localisation
accept_language = 'en-AU'

# In practice, retrieve and cache an access token dynamically at runtime.
# https://developer.seek.com/auth
access_token = ''

# In practice, use your application name and version from configuration.
# https://developer.seek.com/graphql/in-practice#tracing-requests
user_agent = 'example-application/1.2.3'

# In practice, share the session ID among requests in a given interaction.
# https://developer.seek.com/graphql/in-practice#tracing-requests
session_id = str(uuid.uuid4())

# In practice, use a GraphQL client.
# https://developer.seek.com/graphql/in-practice#client-libraries-and-tools
# https://graphql.org/community/tools-and-libraries/?tags=client_python
url = 'https://graphql.seek.com/graphql'
headers = {
  'Accept-Language': accept_language,
  'Authorization': f'Bearer {access_token}',
  'Content-Type': 'application/json',
  'User-Agent': user_agent,
  'X-Request-Id': str(uuid.uuid4()),
  'X-Session-Id': session_id
}
# In practice, be wary of code injection.
# https://developer.seek.com/graphql/in-practice#variables
data = json.dumps({
  'query': '{ version }'
}).encode('utf-8')
request = urllib.request.Request(url, data, headers)

with urllib.request.urlopen(request) as response:
  response_body = response.read().decode('utf-8')

print(response.status)
# 200

print(response_body)
# {"data":{"version":"abcdefa.12345"},"extensions":{"requestLatency":1}}
```

```swift
import Foundation
import FoundationNetworking

// In practice, propagate language preferences from the end-user client.
// https://developer.seek.com/graphql/in-practice#content-localisation
let acceptLanguage = "en-AU"

// In practice, retrieve and cache an access token dynamically at runtime.
// https://developer.seek.com/auth
let accessToken = ""

// In practice, use your application name and version from configuration.
// https://developer.seek.com/graphql/in-practice#tracing-requests
let userAgent = "example-application/1.2.3"

// In practice, share the session ID among requests in a given interaction.
// https://developer.seek.com/graphql/in-practice#tracing-requests
let sessionId = UUID().uuidString

// In practice, use a GraphQL client.
// https://developer.seek.com/graphql/in-practice#client-libraries-and-tools
// https://graphql.org/community/tools-and-libraries/?tags=client_swift-objective-c-ios
let url = URL(string: "https://graphql.seek.com/graphql")!
var request = URLRequest(url: url)
request.httpMethod = "POST"
request.addValue(acceptLanguage, forHTTPHeaderField: "Accept-Language")
request.addValue("Bearer \(accessToken)", forHTTPHeaderField: "Authorization")
request.addValue("application/json", forHTTPHeaderField: "Content-Type")
request.addValue(userAgent, forHTTPHeaderField: "User-Agent")
request.addValue(UUID().uuidString, forHTTPHeaderField: "X-Request-Id")
request.addValue(sessionId, forHTTPHeaderField: "X-Session-Id")
// In practice, be wary of code injection.
// https://developer.seek.com/graphql/in-practice#variables
request.httpBody = try JSONSerialization.data(withJSONObject: ["query": "{ version }"])

// In practice, properly handle concurrency and errors.
let semaphore = DispatchSemaphore(value: 0)

let task = URLSession.shared.dataTask(with: request) { (data, response, error) in
  print((response as! HTTPURLResponse).statusCode)
  // 200

  print(String(data: data!, encoding: .utf8)!)
  // {"data":{"version":"abcdefa.12345"},"extensions":{"requestLatency":1}}

  semaphore.signal()
}

task.resume()

semaphore.wait()
```

import { PageIcon } from '../components/PageIcon/PageIcon';

The above samples send an unauthenticated [`version`] query.
A valid access token is required in the `Authorization` request header for other operations,
as covered in the previous [<PageIcon icon="Auth" /> Auth][auth] section.

[`version`]: /schema/query/version
[auth]: /auth#client-credentials

## Code samples

We provide GraphQL code samples across the Developer Site to demonstrate the implementation of features that are described in the documentation:

```graphql
query ($id: String!) {
  hiringOrganization(id: $id) {
    name
  }
}
```

```json
{
  "id": "seekAnzPublicTest:organization:seek:93WyyF1h"
}
```

```json
{
  "hiringOrganization": {
    "name": "Acme Corp"
  }
}
```

There are a few aspects to each code sample:

1. The first tab displays the query or mutation [operation](/graphql/in-practice#operations) that is being described.

2. The second tab displays the input [variables](/graphql/in-practice#variables) that are supplied alongside the operation.

3. The third tab displays the result of executing the operation with the supplied variables.

4. The Copy button copies the contents of the current tab to your clipboard.

   This may be useful to incorporate the sample code into your software or an API testing tool.

5. The GraphQL Explorer button takes you to a [dashboard](#dashboard) page where you can execute the operation.

   The dashboard includes a lightweight, in-browser editor where you can execute GraphQL operations in one click.
   It is similar in usage to API testing tools like Postman and Insomnia.

   Our code samples reference [mock objects] in our read-only [Playground] environment.
   This allows us to document operations that can be executed by all partners;
   by contrast, access to production objects is typically restricted to a single hirer or partner.

[mock objects]: /graphql/playground/mock-objects
[Playground]: /graphql/playground

## Dashboard

The Developer Dashboard is an administrative user interface that lets you observe and configure your SEEK API integration:

import { Actions, ButtonLink, IconArrow } from 'braid-design-system';

<Actions>
  <ButtonLink
    href="/manage"
    icon={<IconArrow direction="right" />}
    variant="soft"
  >
    Dashboard
  </ButtonLink>
</Actions>

It’s a key component of our [integration process] and can be used to:

- Interact with [Job Posting] feature references
- Manage your [webhook subscriptions]
- Configure notification channels for release notes and system health
- Create and rotate your [client credentials]
- Run [Playground] operations in the GraphQL Explorer

## Schema

The schema documentation website lets you inspect low-level details of our GraphQL operations & types:

<Actions>
  <ButtonLink href="/schema" icon={<PageIcon icon="Schema" />} variant="soft">
    Schema
  </ButtonLink>
</Actions>

This documentation is automatically generated from our GraphQL schema.
You can [introspect] the schema yourself by pointing GraphQL client tooling at `https://graphql.seek.com/graphql`.
See [GraphQL in practice] for more information about our GraphQL endpoint and client tooling.

[candidates]: /schema/named-type/Candidate
[client credentials]: /auth#client-credentials
[integration process]: /introduction#integration-process
[GraphQL]: https://graphql.org/
[GraphQL in practice]: /graphql/in-practice
[Introduction to GraphQL]: https://graphql.org/learn/
[introspect]: https://graphql.org/learn/introspection/
[Job Posting]: /use-cases/job-posting
[Playground]: /graphql/playground
[positions]: /schema/named-type/PositionOpening
[webhook subscriptions]: /events/webhooks
